import React, { useRef, useState } from "react";
import { Upload, X, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Signed Cloudinary uploader.
 * Props:
 *  - folder: e.g. "bookings/tmp/" or "services/hero/"
 *  - value:  array of attachment objects [{url, public_id, resource_type, original_filename, bytes}]
 *  - onChange: (arr) => void
 *  - max: maximum count (default 5)
 *  - accept: HTML accept attribute (default image and pdf)
 *  - variant: "light" (on cream bg) | "dark" (on dark bg)
 *  - label: string
 */
export default function FileUploader({
  folder = "bookings/tmp/",
  value = [],
  onChange,
  max = 5,
  accept = "image/*,application/pdf",
  variant = "light",
  label = "Attach files",
  "data-testid": testId = "file-uploader",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const isDark = variant === "dark";

  const uploadOne = async (file) => {
    // Get signature
    const isImage = file.type.startsWith("image/");
    const resource_type = isImage ? "image" : "raw";
    const { data: sig } = await api.get("/uploads/signature", {
      params: { folder, resource_type },
    });

    const form = new FormData();
    form.append("file", file);
    form.append("api_key", sig.api_key);
    form.append("timestamp", sig.timestamp);
    form.append("signature", sig.signature);
    form.append("folder", sig.folder);

    const endpoint = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resource_type}/upload`;
    const res = await fetch(endpoint, { method: "POST", body: form });
    if (!res.ok) throw new Error("Cloudinary upload failed");
    const data = await res.json();
    return {
      url: data.secure_url,
      public_id: data.public_id,
      resource_type,
      original_filename: file.name,
      bytes: data.bytes,
    };
  };

  const handleFiles = async (files) => {
    const list = Array.from(files);
    if (!list.length) return;
    if (value.length + list.length > max) {
      toast.error(`Maximum ${max} files`);
      return;
    }
    setUploading(true);
    const out = [...value];
    try {
      for (const f of list) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name} exceeds 10 MB`);
          continue;
        }
        try {
          const att = await uploadOne(f);
          out.push(att);
        } catch (err) {
          const msg = formatApiError(err.response?.data?.detail) || err.message;
          toast.error(`Failed to upload ${f.name}: ${msg}`);
        }
      }
      onChange?.(out);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => onChange?.(value.filter((_, i) => i !== idx));

  const dropZoneCls = isDark
    ? `border-dashed border ${dragOver ? "border-[#C5A059] bg-[#C5A059]/10" : "border-white/25 hover:border-[#C5A059]/60"} p-6 text-center cursor-pointer transition-colors`
    : `border-dashed border ${dragOver ? "border-[#C5A059] bg-[#C5A059]/5" : "border-[#DCD3C0] hover:border-[#C5A059]/60 bg-white"} p-6 text-center cursor-pointer transition-colors`;

  const textMain = isDark ? "text-[#F5EFE4]" : "text-[#1A1815]";
  const textMuted = isDark ? "text-[#F5EFE4]/60" : "text-[#6B665E]";

  return (
    <div data-testid={testId} className="space-y-3">
      <label className={`luxor-label ${isDark ? "!text-[#F5EFE4]" : ""}`}>{label}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={dropZoneCls}
        data-testid={`${testId}-dropzone`}
      >
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          accept={accept}
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testId}-input`}
        />
        {uploading ? (
          <div className={`flex items-center justify-center gap-3 ${textMain}`}>
            <Loader2 size={18} className="animate-spin" /> Uploading…
          </div>
        ) : (
          <>
            <Upload size={18} className={`mx-auto ${isDark ? "text-[#C5A059]" : "text-[#C5A059]"}`} />
            <p className={`mt-2 text-sm ${textMain}`}>Click or drag files here</p>
            <p className={`text-xs ${textMuted}`}>
              Up to {max} files · Images or PDF · Max 10&nbsp;MB each
            </p>
          </>
        )}
      </div>

      {value.length > 0 && (
        <ul className="space-y-2" data-testid={`${testId}-list`}>
          {value.map((att, i) => (
            <li
              key={att.public_id + i}
              className={`flex items-center gap-3 p-2 border ${isDark ? "border-white/10 bg-white/5" : "border-[#E4D9C1] bg-white"}`}
            >
              {att.resource_type === "image" ? (
                <img src={att.url} alt="" className="w-10 h-10 object-cover" />
              ) : (
                <div className={`w-10 h-10 flex items-center justify-center ${isDark ? "bg-white/5" : "bg-[#F5EFE4]"}`}>
                  <FileText size={16} className="text-[#C5A059]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm truncate ${textMain}`}>{att.original_filename || att.public_id}</p>
                <p className={`text-[10px] uppercase tracking-[0.18em] ${textMuted}`}>
                  {att.resource_type} · {att.bytes ? Math.round(att.bytes / 1024) + " KB" : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`${testId}-remove-${i}`}
                className={`p-1 ${isDark ? "text-[#F5EFE4]/60 hover:text-red-400" : "text-[#6B665E] hover:text-red-600"}`}
                aria-label="Remove"
              >
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
